import { useRef } from 'react'

import {
  EVENT_NAME_POINTER_CANCEL,
  EVENT_NAME_POINTER_MOVE,
  EVENT_NAME_POINTER_UP,
} from '../../../../../constants/event'
import { preventDefault, stopPropagation } from '../../../../../utils/events'
import { BirdviewChartGridAxis } from '../../BirdviewChartGrid.constants'
import {
  checkIsBirdviewChartGridColumnAxis,
  getBirdviewChartGridResizedSpan,
  getBirdviewChartGridStep,
} from '../../BirdviewChartGrid.utils'

export function useBirdviewChartGridResize({
  columnSpan,
  rowSpan,
  onResizeColumn,
  onResizeRow,
}) {
  const itemRef = useRef(null)

  function startResize({ axis, event }) {
    preventDefault(event)
    stopPropagation(event)

    // Pointer capture keeps hover and tooltips off the charts crossed.
    event.currentTarget.setPointerCapture?.(event.pointerId)

    const rect = itemRef.current?.getBoundingClientRect()
    const isColumn = checkIsBirdviewChartGridColumnAxis(axis)
    const start = isColumn ? event.clientX : event.clientY
    const size = rect ? (isColumn ? rect.width : rect.height) : 0
    const span = isColumn ? columnSpan : rowSpan
    const step = getBirdviewChartGridStep({ axis, size, span })

    function handleMove(moveEvent) {
      if (step > 0) {
        const nextSpan = getBirdviewChartGridResizedSpan({
          axis,
          delta: (isColumn ? moveEvent.clientX : moveEvent.clientY) - start,
          span,
          step,
        })

        if (isColumn) {
          onResizeColumn(nextSpan)
        } else {
          onResizeRow(nextSpan)
        }
      }
    }

    function handleUp() {
      window.removeEventListener(EVENT_NAME_POINTER_MOVE, handleMove)
      window.removeEventListener(EVENT_NAME_POINTER_UP, handleUp)
      window.removeEventListener(EVENT_NAME_POINTER_CANCEL, handleUp)
    }

    window.addEventListener(EVENT_NAME_POINTER_MOVE, handleMove)
    window.addEventListener(EVENT_NAME_POINTER_UP, handleUp)
    window.addEventListener(EVENT_NAME_POINTER_CANCEL, handleUp)
  }

  function handleColumnResizeStart(event) {
    startResize({ axis: BirdviewChartGridAxis.Column, event })
  }

  function handleRowResizeStart(event) {
    startResize({ axis: BirdviewChartGridAxis.Row, event })
  }

  return { handleColumnResizeStart, handleRowResizeStart, itemRef }
}
